import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Select,
  InputNumber,
  DatePicker,
  message,
  Space,
  Row,
  Col,
  Divider,
  Spin,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  CarOutlined,
  ToolOutlined,
  DeleteOutlined,
  PlusOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;

const VanEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);

  const fetchVan = useCallback(async () => {
    try {
      setFetching(true);
      setError(null);
      const response = await apiService.vans.getById(id);
      console.log('Van response:', response);

      const van = response.data;
      if (!van) {
        throw new Error('Van not found');
      }

      // Convert dates to dayjs objects, but don't include maintenance history
      const formattedVan = {
        ...van,
        lastMaintenance: van.lastMaintenance ? dayjs(van.lastMaintenance) : null,
        nextService: van.nextService ? dayjs(van.nextService) : null,
        maintenanceHistory: [] // Reset maintenance history to empty array
      };

      form.setFieldsValue(formattedVan);
    } catch (error) {
      console.error('Error fetching van:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch van details');
      message.error('Failed to fetch van details');
    } finally {
      setFetching(false);
    }
  }, [id, form]);

  useEffect(() => {
    fetchVan();
  }, [fetchVan]);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const formattedValues = {
        ...values,
        lastMaintenance: values.lastMaintenance?.toISOString(),
        nextService: values.nextService?.toISOString(),
        // Only include new maintenance records
        maintenanceHistory: values.maintenanceHistory?.map(record => ({
          ...record,
          date: record.date.toISOString()
        })) || []
      };
      
      await apiService.vans.update(id, formattedValues);
      message.success('Van updated successfully');
      navigate('/vans');
    } catch (error) {
      console.error('Error updating van:', error);
      message.error(error.response?.data?.message || 'Failed to update van');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <CarOutlined />
            <span>Edit Van</span>
          </Space>
        }
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/vans')}
          >
            Back to List
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="registration"
                label="Registration Number"
                rules={[{ required: true, message: 'Please enter registration number' }]}
              >
                <Input prefix={<CarOutlined />} placeholder="Enter registration number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="available">Available</Option>
                  <Option value="in-use">In Use</Option>
                  <Option value="maintenance">Maintenance</Option>
                  <Option value="not available">Not Available</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="make"
                label="Make"
                rules={[{ required: true, message: 'Please enter make' }]}
              >
                <Input placeholder="Enter make" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="model"
                label="Model"
                rules={[{ required: true, message: 'Please enter model' }]}
              >
                <Input placeholder="Enter model" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="year"
                label="Year"
                rules={[{ required: true, message: 'Please enter year' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1900}
                  max={new Date().getFullYear()}
                  placeholder="Enter year"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="capacity"
                label="Capacity"
                rules={[{ required: true, message: 'Please enter capacity' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  placeholder="Enter capacity"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="vanType"
                label="Van Type"
                rules={[{ required: true, message: 'Please select van type' }]}
              >
                <Select placeholder="Select van type">
                  <Option value="company_van">Company Van</Option>
                  <Option value="rental">Rental</Option>
                  <Option value="finance">Finance</Option>
                  <Option value="contract_van">Contract Van</Option>
                  <Option value="external_van">External Van</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Space>
              <ToolOutlined />
              <span>Maintenance Information</span>
            </Space>
          </Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="lastMaintenance"
                label="Last Maintenance"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select last maintenance date"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nextService"
                label="Next Service"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select next service date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Space>
              <HistoryOutlined />
              <span>Add Maintenance Record</span>
            </Space>
          </Divider>

          <Form.List name="maintenanceHistory">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, 'date']}
                        rules={[{ required: true, message: 'Date is required' }]}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, 'mileage']}
                        rules={[{ required: true, message: 'Mileage is required' }]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="Mileage"
                          min={0}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, 'nextServiceMileage']}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="Next Service Mileage"
                          min={0}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'description']}
                        rules={[{ required: true, message: 'Description is required' }]}
                      >
                        <Input placeholder="Description" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Maintenance Record
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter any additional notes"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Update Van
              </Button>
              <Button onClick={() => navigate('/vans')}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default VanEdit; 